import { Type } from "@themes/pollinator/ui/Type";
import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import type { Distribution } from "./orgInsights.types";

export type Props = {
  /** Section title, e.g. "Affinities". */
  title: string;
  /** Description shown under the title. */
  description?: string;
  /**
   * Org-side distribution. Buckets are independent (multi-select):
   * percentages do not sum to 100.
   *
   * Omit (below-threshold state) to render only the program column.
   */
  orgData?: Distribution;
  /** Program-wide distribution — always shown. */
  programData: Distribution;
  /** Sample size for the org side, surfaced as "n = X". */
  orgN?: number;
  /** Sample size for the program side, surfaced as "n = X". */
  programN: number;
  /**
   * Label for the org column header. Defaults to "Your org" when no
   * specific org name is available.
   */
  orgLabel?: string;
};

/**
 * Side-by-side bucket comparison for multi-select metrics where slices
 * don't sum to 100 (a person can be in multiple buckets), so a donut
 * misrepresents the data. We list each bucket with the org %  alongside
 * the Pollinator-wide %.
 *
 * Buckets are sorted by program-wide percentage descending so the most
 * prevalent categories appear first.
 */
export function OrgInsightsComparisonList({
  title,
  description,
  orgData,
  programData,
  orgN,
  programN,
  orgLabel = "Your org",
}: Props) {
  const labels = Object.keys(programData).sort(
    (a, b) => programData[b] - programData[a]
  );

  // When there's no org data (below-threshold state), collapse to a single
  // column with just the program-wide percentages. Showing a dangling
  // column of "—" looked broken and gave away no information.
  const hasOrgColumn = orgData != null;
  const gridCols = hasOrgColumn
    ? "grid-cols-[1fr_auto_auto]"
    : "grid-cols-[1fr_auto]";

  return (
    <SectionHeading
      title={title}
      description={description}
      titleVariant="h4"
      descriptionVariant="body2"
    >
      <div className="flex flex-col gap-2">
        <div className={`grid ${gridCols} gap-x-6 gap-y-1`}>
          <Type variant="caption" className="text-brand-700">
            Category
          </Type>
          {hasOrgColumn && (
            <Type variant="caption" className="text-brand-700 text-right">
              {orgLabel}
              {orgN != null ? ` (n=${orgN})` : ""}
            </Type>
          )}
          <Type variant="caption" className="text-brand-700 text-right">
            Pollinator (n={programN})
          </Type>
          {labels.map((label) => (
            <ComparisonRow
              key={label}
              label={label}
              orgValue={orgData?.[label]}
              programValue={programData[label]}
              hasOrgColumn={hasOrgColumn}
            />
          ))}
        </div>
      </div>
    </SectionHeading>
  );
}

function ComparisonRow({
  label,
  orgValue,
  programValue,
  hasOrgColumn,
}: {
  label: string;
  orgValue: number | undefined;
  programValue: number;
  hasOrgColumn: boolean;
}) {
  return (
    <>
      <Type variant="body2" as="span">
        {label}
      </Type>
      {hasOrgColumn && (
        <Type variant="body2" as="span" className="text-right">
          {orgValue != null ? `${formatPct(orgValue)}%` : "—"}
        </Type>
      )}
      <Type
        variant="body2"
        as="span"
        className="text-right text-brand-700"
      >
        {formatPct(programValue)}%
      </Type>
    </>
  );
}

/** Round to one decimal; drop trailing ".0" so "47.0" reads as "47". */
function formatPct(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

export default OrgInsightsComparisonList;
